import { useEffect, useState } from 'react';

declare global {
    interface Window {
        grecaptcha: {
            ready: (callback: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        };
    }
}

interface RecaptchaConfig {
    siteKey: string;
    enabled: boolean;
}

const DEFAULT_RECAPTCHA_ACTION = 'submit_form';

const normalizeRecaptchaAction = (action: string): string => {
    const normalized = (action || '')
        .replace(/[^A-Za-z/_]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');

    return normalized || DEFAULT_RECAPTCHA_ACTION;
};

/**
 * Hook for managing Google reCAPTCHA v3
 */
export const useRecaptcha = (config: RecaptchaConfig) => {
    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!config.enabled || !config.siteKey) {
            setIsReady(true);
            return;
        }

        // Check if reCAPTCHA script is already loaded
        if (window.grecaptcha) {
            window.grecaptcha.ready(() => {
                setIsReady(true);
            });
            return;
        }

        // Load reCAPTCHA script
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${config.siteKey}`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
            if (window.grecaptcha) {
                window.grecaptcha.ready(() => {
                    setIsReady(true);
                });
            }
        };

        script.onerror = () => {
            console.error('Failed to load reCAPTCHA script');
            setIsReady(true); // Continue without reCAPTCHA
        };

        document.head.appendChild(script);

        return () => {
            // Cleanup: remove script if component unmounts
            const existingScript = document.querySelector(
                `script[src^="https://www.google.com/recaptcha/api.js"]`
            );
            if (existingScript) {
                existingScript.remove();
            }
        };
    }, [config.enabled, config.siteKey]);

    /**
     * Execute reCAPTCHA and get token
     */
    const executeRecaptcha = async (action: string): Promise<string | null> => {
        if (!config.enabled || !config.siteKey) {
            return null;
        }

        if (!isReady) {
            console.warn('reCAPTCHA not ready yet');
            return null;
        }

        try {
            setIsLoading(true);
            const normalizedAction = normalizeRecaptchaAction(action);
            if (normalizedAction !== action) {
                console.warn('reCAPTCHA action normalized:', { originalAction: action, normalizedAction });
            }

            const token = await window.grecaptcha.execute(config.siteKey, { action: normalizedAction });
            return token;
        } catch (error) {
            console.error('reCAPTCHA execution failed:', error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isReady,
        isLoading,
        executeRecaptcha
    };
};
